import clsx from 'clsx';
import { useState } from 'react';

import {
  TaskInstagramFeed,
  TaskInstagramReel,
  TaskInstagramStories,
  TaskTikTokFeed,
  TaskXPost,
  TaskYoutubeFeed,
} from '@/api/spirit';
import { ReactComponent as DownArrowIcon } from '@/assets/icons/bx-chevron-down.svg';

type PostProps = {
  task: TaskInstagramFeed | TaskInstagramReel | TaskInstagramStories | TaskXPost | TaskYoutubeFeed | TaskTikTokFeed;
  title: string;
  imagePath: string;
};

export const Post = (props: PostProps) => {
  const [isToggle, setToggle] = useState<boolean>(false);

  return (
    <div className="flex justify-center gap-3">
      <div className="flex flex-col items-center text-sm">
        <div className="flex aspect-square w-10 items-center justify-center rounded-full bg-white">
          <img src={props.imagePath} alt="" className="w-5" />
        </div>
        <div className={clsx('w-[2px] bg-gray-300', isToggle ? 'h-full' : 'h-[50px]')} />
      </div>
      <div className="w-[190px]">
        <h5 className="mb-2 font-semibold leading-5 ">{props.title}</h5>
        <p className="mb-2 text-xs">
          {props.task.startedAt} ~ {props.task.endedAt}
        </p>
        {isToggle && (
          <div className="mb-2 w-[190px] rounded-lg bg-gray-100">
            {'imageNum' in props.task && 'movieNum' in props.task && props.task.imageNum && props.task.movieNum ? (
              <div className="mb-2 gap-2">
                <p className="text-sm font-semibold">投稿方法</p>
                <p className="text-xs">画像: {props.task.imageNum}枚</p>
                <p className="text-xs">動画: {props.task.movieNum}枚</p>
              </div>
            ) : (
              <></>
            )}
            <div className="mb-2 gap-2">
              <p className="text-sm font-semibold">投稿依頼内容</p>
              <p className="text-xs">{props.task.body}</p>
            </div>
            {'publicationWeek' in props.task && props.task.publicationWeek ? (
              <div className="mb-2 gap-2">
                <p className="text-sm font-semibold">ハイライト</p>
                <p className="text-xs">{String(props.task.publicationWeek)} week</p>
              </div>
            ) : (
              <></>
            )}
            <div className="mb-2">
              <p className="text-sm font-semibold">投稿に含める広告表示</p>
              <div className="mb-1 flex flex-wrap gap-2">
                <div className="whitespace-nowrap rounded-sm bg-white text-xs">{props.task.postingAd}</div>
                {!('taskType' in props.task) && (
                  <>
                    <p className="mb-1 text-xs">※タイアップ投稿ラベルをONにして投稿してください。</p>
                    <p className="text-xs">※「ブランドパートナーを追加」はなし</p>
                  </>
                )}
              </div>
            </div>
            <div className="mb-2 gap-2">
              <p className="whitespace-nowrap text-sm font-semibold">投稿に含めるハッシュタグ(#)</p>
              {props.task.isSecretHashTag ? (
                <p className="text-xs">※採用された方のみお知らせ</p>
              ) : (
                <>
                  <p className="text-xs font-semibold">必須</p>
                  <div className="mb-1 flex flex-wrap gap-2">
                    {props.task.requiredHashTags &&
                      props.task.requiredHashTags.map((value, index) => {
                        return (
                          <div key={index + value} className="whitespace-nowrap rounded-sm bg-white text-xs">
                            {value}
                          </div>
                        );
                      })}
                  </div>
                  {props.task.OptionalHashTags && props.task.OptionalHashTags.length && (
                    <>
                      <p className="text-xs font-semibold">任意</p>
                      <div className="mb-1 flex flex-wrap gap-2">
                        {props.task.OptionalHashTags.map((value, index) => {
                          return (
                            <div key={index + value} className="whitespace-nowrap rounded-sm bg-white text-xs">
                              {value}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            {props.task.accountTags && props.task.accountTags.length && (
              <div className="mb-2 gap-2">
                <p className="whitespace-nowrap text-sm font-semibold">投稿に含めるアカウントタグ(@)</p>
                {props.task.isSecretAccountTag ? (
                  <p className="text-xs">※採用された方のみお知らせ</p>
                ) : (
                  <div className="mb-1 flex flex-wrap gap-2">
                    {props.task.accountTags.map((value, index) => {
                      return (
                        <div key={index + value} className="whitespace-nowrap rounded-sm bg-white text-xs">
                          {value}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {'url' in props.task && (
              <div className="mb-2 gap-2">
                <p className="whitespace-nowrap text-sm font-semibold">投稿に含めるURL</p>
                {props.task.url ? (
                  <p className="text-xs">※採用された方のみお知らせ</p>
                ) : (
                  <p className="text-xs">task.url</p>
                )}
              </div>
            )}
            {'location' in props.task && props.task.location && (
              <div className="mb-2 gap-2">
                <p className="whitespace-nowrap text-sm font-semibold">投稿に含める位置情報</p>
                <p className="text-xs">
                  {props.task.isSecretLocation ? (
                    <p className="text-xs">※採用された方のみお知らせ</p>
                  ) : (
                    props.task.location
                  )}
                </p>
              </div>
            )}
          </div>
        )}
        <button
          onClick={() => setToggle((value: boolean) => !value)}
          className="mx-auto my-0 mb-2 flex w-[190px] text-blue-500 underline focus:outline-none"
        >
          <span className="inline-block items-center text-xs">{isToggle ? '最小に戻す' : 'もっと見る'}</span>
          <DownArrowIcon
            height={16}
            width={16}
            fill="#3b82f6"
            className={`text-blue-500 transition-transform duration-300 ${isToggle ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
    </div>
  );
};
